/**
 * KPI Metadata Registry
 *
 * Central configuration for all KPI metrics across asset types
 * Used for dynamic chart generation and display configuration
 */

import type { AssetType } from '../types/chart.types';

export interface KPIMetadata {
  kpiName: string;
  displayName: string;
  description: string;
  unit: string;
  assetType: AssetType;
  chartType: 'line' | 'area' | 'bar';
  color: string;
  yAxisDomain?: [number, number] | [number, 'auto'] | ['auto', 'auto'];
  thresholds?: {
    warning?: { value: number; label: string; color: string };
    critical?: { value: number; label: string; color: string };
  };
}

/**
 * KPI Registry - Complete metadata for all supported KPIs
 */
export const KPI_REGISTRY: Record<string, KPIMetadata> = {
  // ===========================================
  // Battery Storage KPIs
  // ===========================================
  'state_of_charge': {
    kpiName: 'state_of_charge',
    displayName: 'State of Charge (SOC)',
    description: 'Battery charge level (0-100%)',
    unit: '%',
    assetType: 'battery_storage',
    chartType: 'line',
    color: '#00D4FF',
    yAxisDomain: [0, 100],
    thresholds: {
      warning: { value: 20, label: 'Low SOC', color: '#F59E0B' },
      critical: { value: 10, label: 'Critical SOC', color: '#EF4444' }
    }
  },
  'state_of_health': {
    kpiName: 'state_of_health',
    displayName: 'State of Health (SOH)',
    description: 'Battery health degradation indicator',
    unit: '%',
    assetType: 'battery_storage',
    chartType: 'line',
    color: '#10B981',
    yAxisDomain: [70, 100],
    thresholds: {
      warning: { value: 85, label: 'Degrading', color: '#F59E0B' },
      critical: { value: 80, label: 'Replace Soon', color: '#EF4444' }
    }
  },
  'round_trip_efficiency': {
    kpiName: 'round_trip_efficiency',
    displayName: 'Round-Trip Efficiency (RTE)',
    description: 'Energy out / Energy in',
    unit: '%',
    assetType: 'battery_storage',
    chartType: 'line',
    color: '#3B82F6',
    yAxisDomain: [75, 100]
  },
  'availability': {
    kpiName: 'availability',
    displayName: 'Availability',
    description: 'Percentage of time system is operational',
    unit: '%',
    assetType: 'battery_storage',
    chartType: 'area',
    color: '#8B5CF6',
    yAxisDomain: [0, 100],
    thresholds: {
      warning: { value: 90, label: 'Below Target', color: '#F59E0B' },
      critical: { value: 85, label: 'Critical Downtime', color: '#EF4444' }
    }
  },
  'cycle_count': {
    kpiName: 'cycle_count',
    displayName: 'Cycle Count',
    description: 'Cumulative charge/discharge cycles',
    unit: 'cycles',
    assetType: 'battery_storage',
    chartType: 'line',
    color: '#F59E0B',
    yAxisDomain: [0, 'auto']
  },
  'power_capacity_mw': {
    kpiName: 'power_capacity_mw',
    displayName: 'Power Capacity',
    description: 'Available power output',
    unit: 'MW',
    assetType: 'battery_storage',
    chartType: 'line',
    color: '#06B6D4',
    yAxisDomain: [0, 'auto']
  },
  'energy_throughput_mwh': {
    kpiName: 'energy_throughput_mwh',
    displayName: 'Energy Throughput',
    description: 'Cumulative energy flow',
    unit: 'MWh',
    assetType: 'battery_storage',
    chartType: 'area',
    color: '#14B8A6',
    yAxisDomain: [0, 'auto']
  },

  // ===========================================
  // Solar PV KPIs
  // ===========================================
  'performance_ratio': {
    kpiName: 'performance_ratio',
    displayName: 'Performance Ratio',
    description: 'Actual output / Expected output',
    unit: '%',
    assetType: 'solar_pv',
    chartType: 'line',
    color: '#FFD700',
    yAxisDomain: [70, 100],
    thresholds: {
      warning: { value: 80, label: 'Underperforming', color: '#F59E0B' },
      critical: { value: 70, label: 'Critical Performance', color: '#EF4444' }
    }
  },
  'generation_kwh': {
    kpiName: 'generation_kwh',
    displayName: 'Generation',
    description: 'Cumulative energy generated',
    unit: 'kWh',
    assetType: 'solar_pv',
    chartType: 'area',
    color: '#F59E0B',
    yAxisDomain: [0, 'auto']
  },
  'irradiance_wm2': {
    kpiName: 'irradiance_wm2',
    displayName: 'Solar Irradiance',
    description: 'Solar radiation intensity',
    unit: 'W/m²',
    assetType: 'solar_pv',
    chartType: 'area',
    color: '#FCD34D',
    yAxisDomain: [0, 1200]
  },
  'capacity_factor': {
    kpiName: 'capacity_factor',
    displayName: 'Capacity Factor',
    description: 'Actual generation vs nameplate capacity',
    unit: '%',
    assetType: 'solar_pv',
    chartType: 'line',
    color: '#FB923C',
    yAxisDomain: [0, 100]
  },
  'inverter_efficiency': {
    kpiName: 'inverter_efficiency',
    displayName: 'Inverter Efficiency',
    description: 'DC to AC conversion efficiency',
    unit: '%',
    assetType: 'solar_pv',
    chartType: 'line',
    color: '#F97316',
    yAxisDomain: [90, 100]
  },

  // ===========================================
  // EV Charging KPIs
  // ===========================================
  'utilization_rate': {
    kpiName: 'utilization_rate',
    displayName: 'Utilization Rate',
    description: 'Percentage of time chargers are in use',
    unit: '%',
    assetType: 'ev_charging',
    chartType: 'bar',
    color: '#3B82F6',
    yAxisDomain: [0, 100]
  },
  'sessions_count': {
    kpiName: 'sessions_count',
    displayName: 'Charging Sessions',
    description: 'Number of completed charging sessions',
    unit: 'sessions',
    assetType: 'ev_charging',
    chartType: 'bar',
    color: '#8B5CF6',
    yAxisDomain: [0, 'auto']
  },
  'energy_delivered_kwh': {
    kpiName: 'energy_delivered_kwh',
    displayName: 'Energy Delivered',
    description: 'Total energy delivered to vehicles',
    unit: 'kWh',
    assetType: 'ev_charging',
    chartType: 'area',
    color: '#10B981',
    yAxisDomain: [0, 'auto']
  },
  'avg_session_duration_min': {
    kpiName: 'avg_session_duration_min',
    displayName: 'Avg Session Duration',
    description: 'Average charging session length',
    unit: 'min',
    assetType: 'ev_charging',
    chartType: 'line',
    color: '#06B6D4',
    yAxisDomain: [0, 'auto']
  },
  'revenue_usd': {
    kpiName: 'revenue_usd',
    displayName: 'Revenue',
    description: 'Charging revenue generated',
    unit: 'USD',
    assetType: 'ev_charging',
    chartType: 'area',
    color: '#10B981',
    yAxisDomain: [0, 'auto']
  },

  // ===========================================
  // EMS Live Metrics (from Azure IoT Hub telemetry)
  // ===========================================

  // Battery Storage — BMS metrics
  'battery_temperature': {
    kpiName: 'battery_temperature',
    displayName: 'Battery Temperature',
    description: 'Battery pack temperature from BMS',
    unit: '°C',
    assetType: 'battery_storage',
    chartType: 'line',
    color: '#F59E0B',
    yAxisDomain: ['auto', 'auto'],
    thresholds: {
      warning: { value: 45, label: 'High Temp', color: '#F59E0B' },
      critical: { value: 55, label: 'Critical Temp', color: '#EF4444' }
    }
  },
  'cell_voltage_min': {
    kpiName: 'cell_voltage_min',
    displayName: 'Min Cell Voltage',
    description: 'Lowest cell voltage in pack',
    unit: 'V',
    assetType: 'battery_storage',
    chartType: 'line',
    color: '#3B82F6',
    yAxisDomain: ['auto', 'auto']
  },
  'cell_voltage_max': {
    kpiName: 'cell_voltage_max',
    displayName: 'Max Cell Voltage',
    description: 'Highest cell voltage in pack',
    unit: 'V',
    assetType: 'battery_storage',
    chartType: 'line',
    color: '#10B981',
    yAxisDomain: ['auto', 'auto']
  },
  'pcs_power_kw': {
    kpiName: 'pcs_power_kw',
    displayName: 'PCS Power',
    description: 'Power conversion system output',
    unit: 'kW',
    assetType: 'battery_storage',
    chartType: 'area',
    color: '#8B5CF6',
    yAxisDomain: ['auto', 'auto']
  },

  // Common electrical metrics
  'active_power_kw': {
    kpiName: 'active_power_kw',
    displayName: 'Active Power',
    description: 'Real power output',
    unit: 'kW',
    assetType: 'battery_storage',
    chartType: 'line',
    color: '#06B6D4',
    yAxisDomain: ['auto', 'auto']
  },
  'reactive_power_kvar': {
    kpiName: 'reactive_power_kvar',
    displayName: 'Reactive Power',
    description: 'Reactive power',
    unit: 'kVAR',
    assetType: 'battery_storage',
    chartType: 'line',
    color: '#A855F7',
    yAxisDomain: ['auto', 'auto']
  },
  'apparent_power_kva': {
    kpiName: 'apparent_power_kva',
    displayName: 'Apparent Power',
    description: 'Apparent power',
    unit: 'kVA',
    assetType: 'battery_storage',
    chartType: 'line',
    color: '#EC4899',
    yAxisDomain: ['auto', 'auto']
  },
  'voltage_v': {
    kpiName: 'voltage_v',
    displayName: 'Voltage',
    description: 'AC voltage',
    unit: 'V',
    assetType: 'meter',
    chartType: 'line',
    color: '#F97316',
    yAxisDomain: ['auto', 'auto']
  },
  'frequency_hz': {
    kpiName: 'frequency_hz',
    displayName: 'Frequency',
    description: 'AC frequency',
    unit: 'Hz',
    assetType: 'meter',
    chartType: 'line',
    color: '#14B8A6',
    yAxisDomain: [49.5, 50.5]
  },

  // Solar PV — live metrics
  'solar_power_kw': {
    kpiName: 'solar_power_kw',
    displayName: 'Solar Power',
    description: 'PV inverter output',
    unit: 'kW',
    assetType: 'solar_pv',
    chartType: 'area',
    color: '#FFD700',
    yAxisDomain: [0, 'auto']
  },

  // Generator metrics
  'generator_power_kw': {
    kpiName: 'generator_power_kw',
    displayName: 'Generator Power',
    description: 'Generator output power',
    unit: 'kW',
    assetType: 'generator',
    chartType: 'area',
    color: '#EF4444',
    yAxisDomain: [0, 'auto']
  },

  // Grid meter metrics
  'grid_power_kw': {
    kpiName: 'grid_power_kw',
    displayName: 'Grid Power',
    description: 'Grid import/export power',
    unit: 'kW',
    assetType: 'meter',
    chartType: 'area',
    color: '#3B82F6',
    yAxisDomain: ['auto', 'auto']
  },
  'grid_frequency_hz': {
    kpiName: 'grid_frequency_hz',
    displayName: 'Grid Frequency',
    description: 'Grid frequency from meter',
    unit: 'Hz',
    assetType: 'meter',
    chartType: 'line',
    color: '#14B8A6',
    yAxisDomain: [49.5, 50.5]
  },
  'device_online': {
    kpiName: 'device_online',
    displayName: 'Device Online',
    description: 'Device connectivity status (1=online, 0=offline)',
    unit: '',
    assetType: 'battery_storage',
    chartType: 'line',
    color: '#22C55E',
    yAxisDomain: [0, 1]
  },
  // BMS comms-loss (djin-cloud#559 / genieos Demsv1.0#1511). 0 = the edge cannot
  // reach the BMS over Modbus (battery data stale, not a real SOC=0); 1 = reachable.
  'bms_online': {
    kpiName: 'bms_online',
    displayName: 'BMS Comms',
    description: 'BMS reachability from the edge (1=reachable, 0=comms down)',
    unit: '',
    assetType: 'battery_storage',
    chartType: 'line',
    color: '#EF4444',
    yAxisDomain: [0, 1]
  }
};

/**
 * Get KPI metadata by name
 */
export function getKPIMetadata(kpiName: string): KPIMetadata | null {
  return KPI_REGISTRY[kpiName] || null;
}

/**
 * Get asset type for a given KPI name
 */
export function getAssetTypeForKPI(kpiName: string): AssetType | null {
  const metadata = getKPIMetadata(kpiName);
  return metadata?.assetType || null;
}

/**
 * Get all KPIs for a specific asset type
 */
export function getKPIsForAssetType(assetType: AssetType): KPIMetadata[] {
  return Object.values(KPI_REGISTRY).filter(
    (metadata) => metadata.assetType === assetType
  );
}

/**
 * Check if a KPI has threshold warnings
 */
export function hasThresholds(kpiName: string): boolean {
  const metadata = getKPIMetadata(kpiName);
  return !!(metadata?.thresholds && (metadata.thresholds.warning || metadata.thresholds.critical));
}
